// src/utils/action-factory.js

import { v4 as uuidv4 } from 'uuid';
import { actionShowValue } from '../forms/ActionShow.js';
import { actionHideValue } from '../forms/ActionHide.js';
import { actionToggleShowHideValue } from '../forms/ActionToggleShowHide.js';
import { actionSeekValue } from '../forms/ActionSeek.js';
import { actionSpinValue } from '../forms/ActionSpin.js';
import { actionOrbitValue } from '../forms/ActionOrbit.js';
import { actionTransformToValue } from '../forms/ActionTransformTo.js';
import { actionTransformByValue } from '../forms/ActionTransformBy.js';
import { actionMoveInDirectionValue } from '../forms/ActionMoveInDirection.js';
import { actionSetMaterialsVariantValue } from '../forms/ActionSetMaterialsVariant.js';
import { actionAnimateValue } from '../forms/ActionAnimate.js';
import { actionPlayParticlesValue } from '../forms/ActionPlayParticles.js';
import { actionOpenUrlValue } from '../forms/ActionOpenUrl.js';
import { actionShowInstructionValue } from '../forms/ActionShowInstruction.js';
import { actionRaiseEventValue } from '../forms/ActionRaiseEvent.js';
import { actionStartFollowingValue } from '../forms/ActionStartFollowing.js';
import { actionForceEndSequenceValue } from '../forms/ActionForceEndSequence.js';

/**
 * Creates a new sequence element object with the specified type and defaults
 * 
 * @param {string} actionType - The type of action to create
 * @param {Object} defaultTarget - The default target object to use
 * @returns {Object} A new action object
 */
export const createSequenceElement = (actionType, defaultTarget = null) => {
    // Common properties for all actions
    const commonProperties = {
        id: uuidv4(),
        type: actionType,
        start: { value: 'after_previous', text: 'After Previous' },
        targets: defaultTarget ? [defaultTarget] : [],
        delay: 0,
        repeat: 0,
    };

    // Type-specific properties
    let specificProperties = {};

    switch (actionType) {
        case 'action_show':
            specificProperties = actionShowValue;
            break;
        case 'action_hide':
            specificProperties = actionHideValue;
            break;
        case 'action_toggle_show_hide':
            specificProperties = actionToggleShowHideValue;
             break;
        case 'action_seek':
            specificProperties = actionSeekValue;
            break;
        case 'action_spin':
            specificProperties = actionSpinValue;
            break;
        case 'action_orbit':
            specificProperties = actionOrbitValue;
            break;
        case 'action_transform_to':
            specificProperties = actionTransformToValue;
            break;
        case 'action_transform_by':
            specificProperties = actionTransformByValue;
            break;
        case 'action_move_in_direction':
            specificProperties = actionMoveInDirectionValue;
            break;
        case 'action_set_materials_variant':
            specificProperties = actionSetMaterialsVariantValue;
            break;
        case 'action_animate':
            specificProperties = actionAnimateValue;
            break;
        case 'action_play_particles':
            specificProperties = actionPlayParticlesValue;
            break;
        case 'action_open_url':
            specificProperties = actionOpenUrlValue;
            break;
        case 'action_show_instructions':
            specificProperties = actionShowInstructionValue;
            break;
        case 'action_raise_event':
            specificProperties = actionRaiseEventValue;
            break;
        case 'action_start_following':
            specificProperties = actionStartFollowingValue;
            break;
        case 'action_force_end_sequence':
            specificProperties = actionForceEndSequenceValue;
            break;
        case 'action_play':
        case 'action_pause':
        case 'action_stop':
        case 'action_toggle_play_pause':
        case 'action_end':
            specificProperties = { wait_until_finished: true };
            break;
        case 'action_history_back':
            specificProperties = { wait_until_finished: false };
            break;
        case 'action_set_experience_origin':
            specificProperties = { wait_until_finished: false };
            break;
        case 'action_stop_following':
            specificProperties = { wait_until_finished: false };
            break;
        default:
            specificProperties = {};
    }

    return {
        ...specificProperties,
        ...commonProperties
    };
};

/**
 * Creates a duplicate of an existing action with a new ID
 * 
 * @param {Object} action - The action to duplicate
 * @returns {Object} A new action object with the same properties but a new ID
 */
export const duplicateAction = (action) => {
    if (!action) return null;

    // Create a deep copy
    const newAction = JSON.parse(JSON.stringify(action));

    // Generate a new ID
    newAction.id = uuidv4();

    // Ensure the duplicated action has appropriate start timing
    // If the original was 'with_previous', the duplicate should also be 'with_previous'
    // to maintain the grouping behavior
    if (!newAction.start) {
        newAction.start = { value: 'after_previous', text: 'After Previous' };
    }

    return newAction;
};